import React from 'react';
import { useTranslation } from 'react-i18next';
import { TASK_GROUPS, roleDefaultTasks } from '../../utils/tasks';

// The "Tasks" section of the admin's Edit Employee form.
//
// `overrides` is { granted: [], removed: [] } relative to `role`. A switch is
// ON when the role has the task and it wasn't removed, or the admin granted
// it. Green "extra" = given beyond the role, red "removed" = taken from it.
// Because overrides are relative, changing the role in the same form simply
// re-bases the switches on the new role's defaults.
const TaskSwitches = ({ role, overrides, onChange }) => {
  const { t } = useTranslation();
  const defaults = roleDefaultTasks(role);
  const granted = overrides.granted || [];
  const removed = overrides.removed || [];

  const isOn = (task) => (defaults.includes(task) ? !removed.includes(task) : granted.includes(task));

  const toggle = (task) => {
    if (defaults.includes(task)) {
      const nextRemoved = removed.includes(task) ? removed.filter((x) => x !== task) : [...removed, task];
      onChange({ granted: granted.filter((x) => x !== task), removed: nextRemoved });
    } else {
      const nextGranted = granted.includes(task) ? granted.filter((x) => x !== task) : [...granted, task];
      onChange({ granted: nextGranted, removed: removed.filter((x) => x !== task) });
    }
  };

  const tag = (task) => {
    const on = isOn(task);
    const isDefault = defaults.includes(task);
    if (on && !isDefault) return { text: t('tasks.extra'), bg: '#dcfce7', color: '#166534' };
    if (!on && isDefault) return { text: t('tasks.removed'), bg: '#fee2e2', color: '#991b1b' };
    return null;
  };

  return (
    <div>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem' }}>
        {t('tasks.sectionTitle')}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.4rem', lineHeight: 1.45 }}>
        {t('tasks.sectionHint')}
      </div>
      <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0.25rem 0.75rem 0.5rem', background: '#fafafa' }}>
        {TASK_GROUPS.map((group) => (
          <div key={group.id}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', margin: '0.6rem 0 0.15rem' }}>
              {t(`tasks.group.${group.id}`)}
            </div>
            {group.tasks.map((task) => {
              const on = isOn(task);
              const badge = tag(task);
              const label = t(`tasks.name.${task.replace('.', '_')}`);
              return (
                <div key={task} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.4rem 0', borderTop: '1px solid #eef2f7' }}>
                  <span style={{ fontSize: '0.82rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {label}
                    {badge && (
                      <span style={{ fontSize: '0.66rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: 6, background: badge.bg, color: badge.color }}>
                        {badge.text}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={label}
                    onClick={() => toggle(task)}
                    style={{
                      position: 'relative', flex: 'none', width: 38, height: 22, borderRadius: 11, border: 'none', padding: 0, cursor: 'pointer',
                      background: on ? '#4f46e5' : '#cbd5e1', transition: 'background 0.15s ease',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.15s ease',
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskSwitches;
